import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  TextField,
  MenuItem,
  Button,
  Alert,
  CircularProgress,
  Typography,
  Chip,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { timeOffAPI, holidaysAPI, employeesAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useTimeOffBalances } from '../../hooks/useTimeOff';
import {
  TIME_OFF_TYPES,
  REQUEST_TYPE_LABELS,
  TIME_OFF_VALIDATION_MESSAGES,
  TIME_OFF_ERROR_MESSAGES,
} from '../../constants';

const requestSchema = z.object({
  type: z.enum([TIME_OFF_TYPES.VACATION, TIME_OFF_TYPES.SICK_DAY, TIME_OFF_TYPES.PERSONAL]),
  startDate: z.string().min(1, TIME_OFF_VALIDATION_MESSAGES.START_DATE_REQUIRED),
  endDate: z.string().min(1, TIME_OFF_VALIDATION_MESSAGES.END_DATE_REQUIRED),
  reason: z.string().optional(),
}).refine((data) => {
  const startDate = new Date(data.startDate);
  const endDate = new Date(data.endDate);
  return endDate >= startDate;
}, {
  message: TIME_OFF_VALIDATION_MESSAGES.END_DATE_AFTER_START,
  path: ["endDate"],
});

type RequestFormData = z.infer<typeof requestSchema>;

interface TimeOffRequestFormProps {
  onSubmit: (request: any) => void;
  onCancel: () => void;
  isAdmin?: boolean;
}

const TimeOffRequestForm: React.FC<TimeOffRequestFormProps> = ({
  onSubmit,
  onCancel,
  isAdmin = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [employees, setEmployees] = useState<any[]>([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string>('');
  const { user } = useAuth();
  const { balances } = useTimeOffBalances();

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm<RequestFormData>({
    resolver: zodResolver(requestSchema),
    defaultValues: {
      type: TIME_OFF_TYPES.VACATION,
      startDate: '',
      endDate: '',
      reason: '',
    },
  });

  const [holidays, setHolidays] = useState<any[]>([]);

  const watchedStartDate = watch('startDate');
  const watchedEndDate = watch('endDate');
  const watchedType = watch('type');

  // Fetch holidays on component mount
  useEffect(() => {
    const fetchHolidays = async () => {
      try {
        const data = await holidaysAPI.getAll();
        setHolidays(data);
      } catch (error) {
        console.error('Failed to fetch holidays:', error);
        setHolidays([]); // Set empty array on error
      }
    };
    fetchHolidays();
  }, []);

  // Fetch employees for admin users and pre-populate with current user
  useEffect(() => {
    const fetchEmployees = async () => {
      if (isAdmin) {
        try {
          const data = await employeesAPI.getActive();
          setEmployees(data);
          
          // Pre-populate with current user's employee ID
          if (user?.id) {
            const currentUserEmployee = data.find(emp => emp.userId === user.id);
            if (currentUserEmployee) {
              setSelectedEmployeeId(currentUserEmployee.id);
            }
          }
        } catch (error) {
          console.error('Failed to fetch employees:', error);
          setEmployees([]);
        }
      }
    };
    fetchEmployees();
  }, [isAdmin, user?.id]);

  // Auto-set end date when start date is selected
  useEffect(() => {
    if (watchedStartDate && !watchedEndDate) {
      setValue('endDate', watchedStartDate);
    }
  }, [watchedStartDate, watchedEndDate, setValue]);

  // Calculate working days (excluding weekends and holidays)
  const workingDays = useMemo(() => {
    if (!watchedStartDate || !watchedEndDate) return 0;
    
    const startDate = new Date(watchedStartDate);
    const endDate = new Date(watchedEndDate);
    
    if (endDate < startDate) return 0;
    
    let workingDays = 0;
    const currentDate = new Date(startDate);
    
    // Create a function to check if a date falls within any holiday range
    const isHoliday = (date: Date) => {
      return holidays
        .filter(h => h.isActive)
        .some(h => {
          const holidayStart = new Date(h.startDate);
          const holidayEnd = new Date(h.endDate);
          holidayStart.setHours(0, 0, 0, 0);
          holidayEnd.setHours(23, 59, 59, 999);
          return date >= holidayStart && date <= holidayEnd;
        });
    };
    
    while (currentDate <= endDate) {
      const dayOfWeek = currentDate.getDay();
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
      const isHolidayDate = isHoliday(currentDate);
      
      if (!isWeekend && !isHolidayDate) {
        workingDays++;
      }
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return workingDays;
  }, [watchedStartDate, watchedEndDate, holidays]);

  // Get current employee's balance
  const currentEmployeeBalance = useMemo(() => {
    if (!balances || balances.length === 0) return null;
    
    // Determine which employee to check
    const targetEmployeeId = isAdmin && selectedEmployeeId 
      ? selectedEmployeeId 
      : user?.employee?.id;
    
    if (!targetEmployeeId) return null;
    
    return balances.find((balance: any) => balance.employeeId === targetEmployeeId);
  }, [balances, isAdmin, selectedEmployeeId, user?.employee?.id]);

  // Check if requested days exceed available days
  const exceedsBalance = useMemo(() => {
    if (!currentEmployeeBalance || workingDays === 0) return false;
    
    let remaining = 0;
    
    if (watchedType === TIME_OFF_TYPES.VACATION) {
      remaining = currentEmployeeBalance.vacationDaysTotal - currentEmployeeBalance.vacationDaysUsed;
    } else if (watchedType === TIME_OFF_TYPES.SICK_DAY) {
      remaining = currentEmployeeBalance.sickDaysTotal - currentEmployeeBalance.sickDaysUsed;
    } else if (watchedType === TIME_OFF_TYPES.PERSONAL) {
      remaining = currentEmployeeBalance.personalDaysTotal - currentEmployeeBalance.personalDaysUsed;
    }
    
    return workingDays > remaining;
  }, [currentEmployeeBalance, workingDays, watchedType]);

  // Get available days for current type
  const availableDays = useMemo(() => {
    if (!currentEmployeeBalance) return null;
    
    if (watchedType === TIME_OFF_TYPES.VACATION) {
      return currentEmployeeBalance.vacationDaysTotal - currentEmployeeBalance.vacationDaysUsed;
    } else if (watchedType === TIME_OFF_TYPES.SICK_DAY) {
      return currentEmployeeBalance.sickDaysTotal - currentEmployeeBalance.sickDaysUsed;
    } else if (watchedType === TIME_OFF_TYPES.PERSONAL) {
      return currentEmployeeBalance.personalDaysTotal - currentEmployeeBalance.personalDaysUsed;
    }
    
    return null;
  }, [currentEmployeeBalance, watchedType]);

  const handleFormSubmit = async (data: RequestFormData) => {
    try {
      setLoading(true);
      setError('');

      const requestData: any = {
        ...data,
        startDate: new Date(data.startDate).toISOString(),
        endDate: new Date(data.endDate).toISOString(),
      };

      // Add employee ID for admin users
      if (isAdmin && selectedEmployeeId) {
        requestData.employeeId = selectedEmployeeId;
      }

      const response = await timeOffAPI.create(requestData);

      // The response is wrapped in a request object
      const responseData = response.request || response;
      onSubmit(responseData);
    } catch (err: any) {
      setError(err.response?.data?.error || TIME_OFF_ERROR_MESSAGES.CREATE_REQUEST);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} noValidate>
      {error && (
        <Alert severity="error" sx={{ my: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            select
            fullWidth
            label="Type of Leave"
            {...register('type')}
            error={!!errors.type}
            helperText={errors.type?.message}
            value={watch('type') || ''}
          >
            <MenuItem value={TIME_OFF_TYPES.VACATION}>{REQUEST_TYPE_LABELS.VACATION}</MenuItem>
            <MenuItem value={TIME_OFF_TYPES.SICK_DAY}>{REQUEST_TYPE_LABELS.SICK_DAY}</MenuItem>
            <MenuItem value={TIME_OFF_TYPES.PERSONAL}>{REQUEST_TYPE_LABELS.PERSONAL}</MenuItem>
          </TextField>

          {isAdmin && (
            <TextField
              select
              fullWidth
              label="Employee"
              value={selectedEmployeeId}
              onChange={(e) => setSelectedEmployeeId(e.target.value)}
            >
              {employees.map((employee) => (
                <MenuItem key={employee.id} value={employee.id}>
                  {employee.firstName} {employee.lastName}
                </MenuItem>
              ))}
            </TextField>
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            fullWidth
            label="Start Date"
            type="date"
            {...register('startDate')}
            error={!!errors.startDate}
            helperText={errors.startDate?.message}
            InputLabelProps={{ shrink: true }}
            onChange={(e) => {
              register('startDate').onChange(e);
              setValue('endDate', e.target.value);
            }}
          />

          <TextField
            fullWidth
            label="End Date"
            type="date"
            {...register('endDate')}
            error={!!errors.endDate}
            helperText={errors.endDate?.message}
            InputLabelProps={{ shrink: true }}
            inputProps={{
              min: watchedStartDate || undefined,
            }}
          />
        </Box>

        {/* Working Days Display */}
        {workingDays > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Working days:
            </Typography>
            <Chip 
              label={`${workingDays} day${workingDays !== 1 ? 's' : ''}`}
              color="primary"
              size="small"
              variant="outlined"
            />
            <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
              (weekends and holidays excluded)
            </Typography>
          </Box>
        )}

        {/* Available Days Display */}
        {availableDays !== null && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Available {REQUEST_TYPE_LABELS[watchedType as keyof typeof REQUEST_TYPE_LABELS]} days:
            </Typography>
            <Chip 
              label={`${availableDays} day${availableDays !== 1 ? 's' : ''}`}
              color={availableDays > 0 ? "success" : "error"}
              size="small"
              variant="outlined"
            />
          </Box>
        )}

        {/* Exceeds Balance Warning */}
        {exceedsBalance && (
          <Alert severity="error">
            You are requesting more days than you have available. You have {availableDays} {REQUEST_TYPE_LABELS[watchedType as keyof typeof REQUEST_TYPE_LABELS]} day{availableDays !== 1 ? 's' : ''} remaining, but are requesting {workingDays} day{workingDays !== 1 ? 's' : ''}.
          </Alert>
        )}

        <TextField
          fullWidth
          label="Reason (Optional)"
          multiline
          rows={3}
          {...register('reason')}
          error={!!errors.reason}
          helperText={errors.reason?.message}
          placeholder="Please provide a reason for your time-off request..."
        />
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
        <Button
          type="submit"
          variant="contained"
          disabled={loading || exceedsBalance}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Submitting...' : 'Submit Request'}
        </Button>
        <Button
          variant="outlined"
          onClick={onCancel}
          disabled={loading}
        >
          Cancel
        </Button>
      </Box>
    </form>
  );
};

export default TimeOffRequestForm; 